/**
 * Clinical Command Registry
 * Defines all available commands for the chat interface
 */

import { ClinicalCommand, ChatContext } from '../types/chat'
import { backendAdapter } from './backendAdapter'

// Re-export ClinicalCommand for other modules
export type { ClinicalCommand } from '../types/chat'

/**
 * Execute HPI generation command
 */
const executeHPICommand = async (context: ChatContext): Promise<any> => {
  if (!context.chartSweep) {
    throw new Error('Chart sweep required. Please run /sweep first.')
  }
  
  return await backendAdapter.executeCommand('/hpi', {
    chartSweep: context.chartSweep,
    edNotes: context.edNotes || ''
  })
}

/**
 * Execute Assessment & Plan command
 */
const executeAssessmentCommand = async (context: ChatContext): Promise<AsyncGenerator> => {
  if (!context.edNotes && !context.chartSweep) {
    throw new Error('Clinical data required. Please provide ED notes or run /sweep first.')
  }
  
  return await backendAdapter.executeCommand('/assessment', {
    hpi: context.edNotes || '',
    chartSweep: context.chartSweep
  })
}

/**
 * Execute Chart Sweep command
 */
const executeSweepCommand = async (context: ChatContext): Promise<any> => {
  if (!context.edNotes) {
    throw new Error('ED notes required for chart sweep.')
  }
  
  return await backendAdapter.executeCommand('/sweep', {
    edNotes: context.edNotes
  })
}

/**
 * Default clinical commands
 * These are pre-loaded and available to all users
 */
export const clinicalCommands: ClinicalCommand[] = [
  {
    trigger: '/hpi',
    name: 'Generate HPI',
    description: 'Create History of Present Illness from chart sweep and ED notes',
    execute: executeHPICommand,
    requiresPatient: true,
    category: 'clinical',
    isDefault: true
  },
  {
    trigger: '/assessment',
    name: 'Generate Assessment & Plan',
    description: 'Create clinical assessment with differential diagnosis and treatment plan',
    execute: executeAssessmentCommand,
    requiresPatient: true,
    category: 'clinical',
    isDefault: true
  },
  {
    trigger: '/ap',
    name: 'Assessment & Plan (Short)',
    description: 'Alias for /assessment',
    execute: executeAssessmentCommand,
    requiresPatient: true,
    category: 'clinical',
    isDefault: true
  },
  {
    trigger: '/sweep',
    name: 'Chart Sweep',
    description: 'Generate clinical snapshot with active issues, orders, and focused exam',
    execute: executeSweepCommand,
    requiresPatient: true,
    category: 'clinical',
    isDefault: true
  },
  {
    trigger: '/chart',
    name: 'Chart Sweep (Alias)',
    description: 'Alias for /sweep',
    execute: executeSweepCommand,
    requiresPatient: true,
    category: 'clinical',
    isDefault: true
  },
  {
    trigger: '/help',
    name: 'Show Commands',
    description: 'Display all available commands and how to use them',
    execute: async () => ({
      type: 'help',
      content: generateHelpContent()
    }),
    requiresPatient: false,
    category: 'utility',
    isDefault: true
  },
  {
    trigger: '/clear',
    name: 'Clear Chat',
    description: 'Clear all messages and reset conversation',
    execute: async () => ({
      type: 'clear',
      confirmed: true
    }),
    requiresPatient: false,
    category: 'utility',
    isDefault: true
  }
]

/**
 * Generate help content showing all available commands
 */
function generateHelpContent(): string {
  const clinicalCmds = clinicalCommands.filter(c => c.category === 'clinical')
  const utilityCmds = clinicalCommands.filter(c => c.category === 'utility')
  
  let help = '# Available Commands\n\n'
  
  help += '## Clinical Commands\n\n'
  clinicalCmds.forEach(cmd => {
    help += `**${cmd.trigger}** - ${cmd.name}\n`
    help += `${cmd.description}\n`
    help += cmd.requiresPatient ? '*Requires patient context*\n\n' : '\n'
  })
  
  help += '## Utility Commands\n\n'
  utilityCmds.forEach(cmd => {
    help += `**${cmd.trigger}** - ${cmd.name}\n`
    help += `${cmd.description}\n\n`
  })
  
  help += '---\n\n'
  help += '*Tip: Type `/` to see all available commands*\n'
  
  return help
}

/**
 * Find command by trigger
 */
export const findCommand = (trigger: string): ClinicalCommand | undefined => {
  return clinicalCommands.find(cmd => 
    cmd.trigger.toLowerCase() === trigger.toLowerCase()
  )
}

/**
 * Extract command from input
 */
export const extractCommand = (input: string): string | null => {
  const match = input.trim().match(/^\/(\w+)/)
  return match ? `/${match[1]}` : null
}
